import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { API } from '../config';
import { useAuth } from '../auth';
import { IconCopy } from './Icons';

// base = URL pubblico configurato (se presente); altrimenti l'origine con cui l'admin apre l'app.
const linkOf = (token, base) => `${(base || window.location.origin).replace(/\/+$/, '')}/p/${token}`;

export default function AccessiPubbliciCard() {
  const { can, user } = useAuth();
  const isAdmin = user?.ruolo === 'admin';
  const [baseUrl, setBaseUrl] = useState('');       // URL pubblico base per link/QR (impostazioni)
  const [savingBase, setSavingBase] = useState(false);
  const [items, setItems] = useState([]);
  const [nome, setNome] = useState('');
  const [tetto, setTetto] = useState(50);
  const [scadenza, setScadenza] = useState('');
  const [capVf, setCapVf] = useState(false);
  const [capCov, setCapCov] = useState(false);
  const [capCovTim, setCapCovTim] = useState(false);
  const [capDom, setCapDom] = useState(false);
  const [capVic, setCapVic] = useState(false);
  const [capInd, setCapInd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [crono, setCrono] = useState(null); // { nome, items } | null
  const [qr, setQr] = useState(null);       // { nome, link } | null : QR del link pubblico

  const load = useCallback(() => {
    axios.get(`${API}/accessi-pubblici`).then(r => setItems(r.data?.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/settings`).then(r => setBaseUrl(r.data?.public_base_url || '')).catch(() => {});
  }, []);

  const salvaBase = async () => {
    setSavingBase(true);
    try {
      await axios.put(`${API}/settings`, { public_base_url: baseUrl });
      setBaseUrl(v => (v || '').trim().replace(/\/+$/, ''));
      toast.success('URL pubblico salvato');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally { setSavingBase(false); }
  };

  const crea = async () => {
    if (!nome.trim()) { toast.info('Inserisci un nome'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/accessi-pubblici`, {
        nome: nome.trim(),
        tetto_giornaliero: Number(tetto) || 50,
        scadenza: scadenza || null,
        cap_vodafone: capVf,
        cap_copertura: capCov,
        cap_coperturatim: capCovTim,
        cap_dominio: capDom,
        cap_vicine: capVic,
        cap_indaga: capInd,
      });
      setNome(''); setTetto(50); setScadenza(''); setCapVf(false); setCapCov(false); setCapCovTim(false); setCapDom(false); setCapVic(false); setCapInd(false);
      toast.success('Accesso creato');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore creazione');
    } finally { setBusy(false); }
  };

  const copia = async (token) => {
    const link = linkOf(token, baseUrl);
    try {
      // navigator.clipboard esiste solo in contesti sicuri (HTTPS/localhost).
      // L'app gira in HTTP su IP: usa il fallback textarea + execCommand.
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(link);
      } else {
        const ta = document.createElement('textarea');
        ta.value = link;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus(); ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (!ok) throw new Error('execCommand copy fallito');
      }
      toast.success('Link copiato');
    } catch {
      // Ultima spiaggia: mostra il link da copiare a mano.
      window.prompt('Copia il link (Ctrl+C, Invio):', link);
    }
  };
  const toggle = async (token) => {
    try { await axios.post(`${API}/accessi-pubblici/${token}/toggle`); load(); } catch { toast.error('Errore'); }
  };
  const rigenera = async (token) => {
    if (!window.confirm('Rigenerare il token? Il vecchio link smetterà di funzionare.')) return;
    try { await axios.post(`${API}/accessi-pubblici/${token}/rigenera`); toast.success('Token rigenerato'); load(); }
    catch { toast.error('Errore'); }
  };
  const elimina = async (token) => {
    if (!window.confirm('Eliminare questo accesso?')) return;
    try { await axios.delete(`${API}/accessi-pubblici/${token}`); load(); } catch { toast.error('Errore'); }
  };
  const apriCrono = async (it) => {
    try {
      const r = await axios.get(`${API}/accessi-pubblici/${it.token}/cronologia`);
      setCrono({ nome: it.nome, items: r.data?.items || [] });
    } catch { toast.error('Errore cronologia'); }
  };

  return (
    <div className="imp-card imp-card--wide">
      <h3 className="imp-card-title">Accessi pubblici (ricerca online)</h3>
      <p style={{ margin: '0 0 14px', color: '#64748b', fontSize: 13 }}>
        Crea un link da mandare via email a un collaboratore: potrà verificare numeri senza login.
        Ogni link ha un tetto giornaliero, anti-raffica (10s) e una scadenza opzionale.
      </p>

      {isAdmin && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10, padding: '10px 12px', marginBottom: 14 }}>
          <label style={{ fontSize: 12, color: '#475569', flex: 1, minWidth: 220 }}>URL pubblico base (per link e QR)<br />
            <input value={baseUrl} onChange={e => setBaseUrl(e.target.value)} placeholder="https://tuodominio.it"
              style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, width: '100%' }} />
          </label>
          <button className="btn" disabled={savingBase} onClick={salvaBase} style={{ height: 38 }}>Salva</button>
          <div style={{ flexBasis: '100%', fontSize: 11.5, color: '#94a3b8' }}>
            Se impostato, i link e i QR usano questo indirizzo (raggiungibile dai telefoni) invece di quello con cui apri l'app. Lascia vuoto per usare l'indirizzo corrente.
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
        <label style={{ fontSize: 12, color: '#475569' }}>Nome collaboratore<br />
          <input value={nome} onChange={e => setNome(e.target.value)} placeholder="Es. Mario Rossi"
            style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, minWidth: 180 }} />
        </label>
        <label style={{ fontSize: 12, color: '#475569' }}>Tetto/giorno<br />
          <input type="number" min="1" value={tetto} onChange={e => setTetto(e.target.value)}
            style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, width: 90 }} />
        </label>
        <label style={{ fontSize: 12, color: '#475569' }}>Scadenza (opz.)<br />
          <input type="date" value={scadenza} onChange={e => setScadenza(e.target.value)}
            style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8 }} />
        </label>
        <button className="btn btn-primary" disabled={busy} onClick={crea} style={{ padding: '0 18px', height: 38 }}>
          Crea link
        </button>
      </div>
      {(can('gea') || can('ssmate_copertura') || can('dominio') || can('vicine') || can('indaga')) && (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', margin: '6px 0', fontSize: 13, color: '#475569' }}>
          <span style={{ color: '#94a3b8', fontSize: 12 }}>Strumenti abilitati sul link:</span>
          {can('gea') && (
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={capVf} onChange={e => setCapVf(e.target.checked)} /> Vodafone
            </label>
          )}
          {can('ssmate_copertura') && (
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={capCov} onChange={e => setCapCov(e.target.checked)} /> Copertura
            </label>
          )}
          {/* Stessa domanda, altra fonte: il sito TIM non usa SalesMate, quindi risponde
              anche quando gli account di sistema hanno la password scaduta. */}
          {can('copertura') && (
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={capCovTim} onChange={e => setCapCovTim(e.target.checked)} /> Copertura sito TIM
            </label>
          )}
          {can('dominio') && (
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={capDom} onChange={e => setCapDom(e.target.checked)} /> Dominio
            </label>
          )}
          {can('vicine') && (
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={capVic} onChange={e => setCapVic(e.target.checked)} /> Vicine
            </label>
          )}
          {can('indaga') && (
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={capInd} onChange={e => setCapInd(e.target.checked)} /> Indaga
            </label>
          )}
        </div>
      )}

      {items.length === 0 ? (
        <div style={{ color: '#94a3b8', fontSize: 13 }}>Nessun accesso creato.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#64748b' }}>
                <th>Nome</th><th>Link</th><th>Usi oggi</th><th>Tetto</th><th>Scadenza</th><th>Stato</th><th></th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.token} style={{ borderTop: '1px solid #f1f5f9', opacity: it.attivo ? 1 : 0.5 }}>
                  <td>{it.nome}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn btn-sm" onClick={() => copia(it.token)} title={linkOf(it.token, baseUrl)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><IconCopy size={13} /> Copia link</button>{' '}
                    <button className="btn btn-sm" onClick={() => setQr({ nome: it.nome, link: linkOf(it.token, baseUrl) })} title="Mostra QR del link">QR</button>
                  </td>
                  <td>{it.usi_oggi}</td>
                  <td>{it.tetto_giornaliero}</td>
                  <td>{it.scadenza || '—'}</td>
                  <td style={{ fontWeight: 700, color: it.attivo ? '#16a34a' : '#94a3b8' }}>{it.attivo ? 'Attivo' : 'Revocato'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {can('gea') && (
                      <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 12, color: '#64748b', marginRight: 8 }}>
                        <input type="checkbox" checked={!!it.cap_vodafone}
                          onChange={async e => { try { await axios.patch(`${API}/accessi-pubblici/${it.token}`, { cap_vodafone: e.target.checked }); } catch { toast.error('Errore aggiornamento'); } load(); }} /> VF
                      </label>
                    )}
                    {can('ssmate_copertura') && (
                      <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 12, color: '#64748b', marginRight: 8 }}>
                        <input type="checkbox" checked={!!it.cap_copertura}
                          onChange={async e => { try { await axios.patch(`${API}/accessi-pubblici/${it.token}`, { cap_copertura: e.target.checked }); } catch { toast.error('Errore aggiornamento'); } load(); }} /> Cop
                      </label>
                    )}
                    {can('copertura') && (
                      <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 12, color: '#64748b', marginRight: 8 }}>
                        <input type="checkbox" checked={!!it.cap_coperturatim}
                          onChange={async e => { try { await axios.patch(`${API}/accessi-pubblici/${it.token}`, { cap_coperturatim: e.target.checked }); } catch { toast.error('Errore aggiornamento'); } load(); }} /> Cop TIM
                      </label>
                    )}
                    {can('dominio') && (
                      <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 12, color: '#64748b', marginRight: 8 }}>
                        <input type="checkbox" checked={!!it.cap_dominio}
                          onChange={async e => { try { await axios.patch(`${API}/accessi-pubblici/${it.token}`, { cap_dominio: e.target.checked }); } catch { toast.error('Errore aggiornamento'); } load(); }} /> Dom
                      </label>
                    )}
                    {can('vicine') && (
                      <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 12, color: '#64748b', marginRight: 8 }}>
                        <input type="checkbox" checked={!!it.cap_vicine}
                          onChange={async e => { try { await axios.patch(`${API}/accessi-pubblici/${it.token}`, { cap_vicine: e.target.checked }); } catch { toast.error('Errore aggiornamento'); } load(); }} /> Vic
                      </label>
                    )}
                    {can('indaga') && (
                      <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center', fontSize: 12, color: '#64748b', marginRight: 8 }}>
                        <input type="checkbox" checked={!!it.cap_indaga}
                          onChange={async e => { try { await axios.patch(`${API}/accessi-pubblici/${it.token}`, { cap_indaga: e.target.checked }); } catch { toast.error('Errore aggiornamento'); } load(); }} /> Ind
                      </label>
                    )}
                    <button className="btn btn-sm" onClick={() => apriCrono(it)} style={{ marginRight: 6 }}>Cronologia</button>
                    <button className="btn btn-sm" onClick={() => toggle(it.token)}>{it.attivo ? 'Revoca' : 'Riattiva'}</button>{' '}
                    <button className="btn btn-sm" onClick={() => rigenera(it.token)}>Rigenera</button>{' '}
                    <button className="btn btn-sm" onClick={() => elimina(it.token)} style={{ color: '#b91c1c' }}>Elimina</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {crono && (
        <div onClick={() => setCrono(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--surface)', borderRadius: 14, padding: 18, width: 'min(640px,96vw)', maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 15 }}>Cronologia · {crono.nome}</h3>
              <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setCrono(null)}>Chiudi</button>
            </div>
            {crono.items.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: 13 }}>Nessuna verifica.</div>
            ) : (
              <table style={{ width: '100%', fontSize: 13 }}>
                <thead><tr style={{ textAlign: 'left', color: '#64748b' }}><th>Tipo</th><th>Numero / Indirizzo</th><th>Esito</th><th>Data</th></tr></thead>
                <tbody>
                  {crono.items.map((d, i) => (
                    <tr key={d.id || i} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td>{d.tipo === 'copertura' ? 'Copertura' : '456'}</td>
                      <td style={{ textTransform: d.tipo === 'copertura' ? 'capitalize' : 'none' }}>
                        {d.tipo === 'copertura'
                          ? `${(d.indirizzo?.comune || '').toLowerCase()} · ${(d.indirizzo?.via || '').toLowerCase()} ${d.indirizzo?.civico || ''}`
                          : d.numero}
                      </td>
                      <td>{d.tipo === 'copertura' ? d.esito : (d.risultato || '—')}</td>
                      <td style={{ whiteSpace: 'nowrap', color: '#64748b' }}>{d.at ? new Date(d.at).toLocaleString('it-IT') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
      {qr && (
        <div onClick={() => setQr(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', zIndex: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--surface)', borderRadius: 14, padding: 20, width: 'min(340px,94vw)', textAlign: 'center' }}>
            <div style={{ fontWeight: 800, marginBottom: 3 }}>{qr.nome}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 14 }}>Inquadra il QR per aprire il link sul telefono</div>
            <div style={{ display: 'inline-block', background: 'var(--surface)', padding: 10, border: '1px solid #e5e7eb', borderRadius: 12 }}>
              <QRCodeSVG value={qr.link} size={220} level="M" />
            </div>
            <div style={{ fontSize: 11, color: '#64748b', wordBreak: 'break-all', margin: '12px 0' }}>{qr.link}</div>
            <button className="btn btn-sm" onClick={() => setQr(null)}>Chiudi</button>
          </div>
        </div>
      )}
    </div>
  );
}
